import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { IMaskModule } from 'angular-imask';
import { map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { IMaskConstants } from '../../../../../../core/constants/imask';
import { CardTypeEnum } from '../../../../../../core/enums/commerce/cards-on-file/card-types';
import { nameOf } from '../../../../../../core/functions/string-helpers';
import { CardOnFileResponse } from '../../../../../../core/models/commerce/cards-on-file/card-on-file-response';
import { SaveCardOnFileRequest } from '../../../../../../core/models/commerce/cards-on-file/save-card-on-file-request';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { RegExPattern } from '../../../../../../core/models/regexp-pattern/regex-pattern';
import { AlertService } from '../../../../../../core/services/alert/alert.service';
import { CreditCardService } from '../../../../../../core/services/commerce/credit-card/credit-card.service';
import { DropdownService } from '../../../../../../core/services/resources/dropdown.service';
import { CardTypeIconComponent } from '../../../../../../shared/components/card-type-icon/card-type-icon.component';
import { DraggableModalWrapperComponent } from '../../../../../../shared/components/modal/draggable-modal-wrapper/draggable-modal-wrapper.component';
import { RegexInputDirective } from '../../../../../../shared/directives/regex-input/regex-input.directive';
import { CardOnFileFormConstants } from '../shared/constants/card-on-file-form-constants';
import { CardOnFileValidationMessages } from '../shared/constants/card-on-file-validation-messages';
import { CardOnFileFormService } from '../shared/services/card-on-file-form.service';
import { EditCardOnFileFormMessages } from './constants/edit-card-on-file-form-messages';
import { EditCardOnFileDisplayInfo } from './models/edit-card-on-file-display-info';
import { EditCardOnFileFormModalData } from './models/edit-card-on-file-form-modal-data';
import { EditCardOnFileFormModel } from './models/edit-card-on-file-form-model';
import { EditCardOnFileFormService } from './services/edit-card-on-file-form.service';

@Component({
    selector: 'app-edit-card-on-file-form-modal',
    templateUrl: './edit-card-on-file-form-modal.component.html',
    styleUrl: '../shared/styles/card-on-file-form-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatCardModule,
        MatButtonModule,
        MatDialogContent,
        MatSelectModule,
        MatDialogActions,
        MatDialogTitle,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        RegexInputDirective,
        IMaskModule,
        MatCheckbox,
        DraggableModalWrapperComponent,
        CardTypeIconComponent,
    ],
})
export class EditCardOnFileFormModalComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    maskOptions = IMaskConstants;
    regExPattern = RegExPattern;
    cardOnFileFormConstants = CardOnFileFormConstants;

    stateCodeOptions: DropdownOption[] = [];
    monthOptions: DropdownOption[] =
        this.dropdownService.getCreditCardMonthOptions();
    yearOptions: DropdownOption[] =
        this.dropdownService.getCreditCardYearOptions();

    /**
     * Read-only card details displayed in the modal but not part of the editable form.
     */
    cardOnFileDisplayInfo: EditCardOnFileDisplayInfo | null = null;

    /**
     * The form group for editing a card on file.
     */
    form: FormGroup<FormGroupType<EditCardOnFileFormModel>> =
        this.editCardOnFileFormService.getFormDefinition();

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: EditCardOnFileFormModalData,
        private readonly alertService: AlertService,
        private readonly dropdownService: DropdownService,
        private readonly creditCardService: CreditCardService,
        private readonly cardOnFileFormService: CardOnFileFormService,
        private readonly editCardOnFileFormService: EditCardOnFileFormService,
        private readonly dialogRef: MatDialogRef<EditCardOnFileFormModalComponent>,
    ) {}

    ngOnInit(): void {
        this.loadForm()
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.loadDropdownOptions();
                }),
                tap(() => {
                    this.setupFormListeners();
                }),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Handles the save action for the edit card on file form. Validates the form and, if valid, saves the updated card on file information to the API. Displays
     * a success alert and closes the modal upon successful save.
     *
     * If the save is successful, the dialog is closed with a result of 'true' to indicate that the card on file was updated, which can be used
     * by the calling component to determine whether to refresh the list of cards on file after the dialog is closed.
     */
    onSave(): void {
        if (this.form.valid) {
            this.saveFormDataToApi()
                .pipe(
                    tap(() => {
                        this.alertService.displaySuccessAlert(
                            EditCardOnFileFormMessages.SUCCESS_ALERT_MESSAGE,
                        );

                        this.dialogRef.close(true);
                    }),
                    takeUntil(this.destroy$),
                )
                .subscribe();
        }
    }

    /**
     * Handles the cancel action for the edit card on file form. Closes the modal without saving any changes.
     *
     * Closing the dialog with a result of 'false' indicates that the operation was cancelled, which can
     * be used by the calling component to determine whether to refresh the list of cards on file or not after
     * the dialog is closed.
     */
    onCancel(): void {
        this.dialogRef.close(false);
    }

    isCardNumberVisa(): boolean {
        return this.cardOnFileDisplayInfo?.cardType === CardTypeEnum.Visa;
    }

    isCardNumberMasterCard(): boolean {
        return this.cardOnFileDisplayInfo?.cardType === CardTypeEnum.Mastercard;
    }

    isCardNumberAmericanExpress(): boolean {
        return this.cardOnFileDisplayInfo?.cardType === CardTypeEnum.Amex;
    }

    getExpirationMonthError(): string | undefined {
        const errors = this.form.controls.expirationMonth.errors;

        if (!errors) {
            return undefined;
        }

        return errors[
            nameOf<typeof CardOnFileValidationMessages>(
                'expirationMonthExpired',
            )
        ] as string | undefined;
    }

    /**
     * Loads the existing card on file information from the API to populate the form for editing.
     * Validates that the user is authenticated and that a card on file ID is provided before making the API call.
     *
     * @throws Error if there is no authenticated user or card on file ID when attempting to load the card on file information for editing.
     * @returns An observable that completes when the card on file information is loaded and the form is populated with the existing data.
     */
    private loadForm(): Observable<void> {
        this.assertRequiredData();

        return this.creditCardService
            .get(this.data.userId, this.data.cardOnFileId)
            .pipe(
                takeUntil(this.destroy$),
                tap((cardOnFileResponse: CardOnFileResponse) => {
                    this.bindForm(cardOnFileResponse);

                    this.bindDisplayInfo(cardOnFileResponse);
                }),
                tap(() => {
                    this.setCardAsDefaultIfOnlyStoredCard();
                }),
                map(() => void 0),
            );
    }

    /**
     * Binds the form with the existing card on file data to populate the form fields for editing.
     */
    private bindForm(response: CardOnFileResponse): void {
        this.editCardOnFileFormService.patchForm(this.form, response);
    }

    /**
     * Extracts and binds the display information from the card on file response to be shown in the modal outside of the form fields.
     */
    private bindDisplayInfo(response: CardOnFileResponse): void {
        this.cardOnFileDisplayInfo = {
            firstName: response.firstName,
            lastName: response.lastName,
            company: response.company,
            truncatedCardNumber: response.truncatedCardNumber,
            cardType: response.cardType,
            isStored: response.isStored,
        };
    }

    /**
     * Saves the form data to the API to update the existing card on file information.
     * Validates that the user is authenticated and that a card on file ID is provided before making the API call.
     *
     * @throws Error if there is no authenticated user or card on file ID when attempting to save the card on file information.
     * @returns An observable that completes when the save operation is finished, returning the response from the update card on file API call.
     */
    private saveFormDataToApi(): Observable<void> {
        this.assertRequiredData();

        return this.creditCardService.update(
            this.data.userId,
            this.data.cardOnFileId,
            this.generateRequestObject(),
        );
    }

    /**
     * Generates the request object to be sent to the API when saving the card on file information.
     *
     * @returns A SaveCardOnFileRequest object containing the data from the form, formatted for the API request.
     */
    private generateRequestObject(): SaveCardOnFileRequest {
        // Use raw value to ensure we include disabled controls
        return {
            ...this.form.getRawValue(),
        } as SaveCardOnFileRequest;
    }

    /**
     * Loads the dropdown options for the form.
     */
    private loadDropdownOptions(): void {
        this.dropdownService
            .getStateCodes()
            .pipe(
                takeUntil(this.destroy$),
                tap((options: DropdownOption[]) => {
                    this.stateCodeOptions = options;
                }),
            )
            .subscribe();
    }

    private setupFormListeners(): void {
        this.cardOnFileFormService.setupExpirationYearListener(
            this.form,
            this.destroy$,
        );
    }

    /**
     * Asserts that the required data is available to load and save information for the edit card on file form modal.
     *
     * @throws Error if card on file ID or authenticated user ID is missing.
     */
    private assertRequiredData(): void {
        if (!this.data?.cardOnFileId) {
            throw new Error(
                'Card on file ID is required to load display information for the edit card on file form modal.',
            );
        }

        if (!this.data?.userId) {
            throw new Error(
                'User ID is required to load display information for the edit card on file form modal.',
            );
        }
    }

    /**
     * If the user has no other stored cards on file, the card being edited must be set as the default payment method.
     * This function checks if there are any other stored cards on file and, if not, sets the card being edited as the
     * default payment method.
     */
    private setCardAsDefaultIfOnlyStoredCard(): void {
        const isEditedCardStored =
            this.cardOnFileDisplayInfo?.isStored === true;

        // If there are no other stored cards on file besides the one being edited, set the card being edited as the default payment method.
        const hasNoOtherStoredCardsOnFile =
            this.data.cardsOnFile.filter(
                (card) => card.isStored && card.id !== this.data.cardOnFileId,
            ).length === 0;

        if (isEditedCardStored && hasNoOtherStoredCardsOnFile) {
            this.cardOnFileFormService.setCardAsDefault(this.form);
        }
    }
}
